import { useState } from 'react';
import { useUnit } from 'effector-react';
import styled, { keyframes } from 'styled-components';
import { Container, Em, Button, Arrow, Field, Label, Input, FieldHint } from '@/components/primitives';
import { loginFx } from '@/model/auth';
import { navigated } from '@/model/router';
import { toastSent } from '@/model/toasts';
import { HttpError } from '@/api';

const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const View = styled.section`
  min-height: 70vh;
  padding: ${({ theme }) => `${theme.spacing[14]} 0`};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[9]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Lede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.body};
  color: ${({ theme }) => theme.ink[3]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[8]};
`;

const ErrorMsg = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

export const Login = () => {
  const pending = useUnit(loginFx.pending);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText(null);
    if (!email.trim() || !password) {
      setErrorText('Введите e-mail и пароль');
      return;
    }
    void loginFx({ email: email.trim(), password }).then(
      () => {
        toastSent('Вы вошли');
        // навигация дальше — через resumeFx в model/search (sample на loginFx.doneData):
        // active search → /processing, completed → /report, else → /onboard.
      },
      (err: unknown) => {
        if (err instanceof HttpError && err.status === 401) {
          setErrorText('Неверный e-mail или пароль');
        } else {
          setErrorText('Не удалось войти. Попробуйте ещё раз.');
        }
      },
    );
  };

  return (
    <View>
      <Container $narrow>
        <Card>
          <Title>
            Войти в <Em>Lume</Em>
          </Title>
          <Lede>Используйте e-mail и пароль, с которыми регистрировались.</Lede>
          <form onSubmit={submit}>
            <Field>
              <Label>E-mail</Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field>
              <Label>Пароль</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <FieldHint>Минимум 8 символов.</FieldHint>
            </Field>
            {errorText && <ErrorMsg>{errorText}</ErrorMsg>}
            <Foot>
              <Button $variant="ghost" type="button" onClick={() => navigated('landing')}>
                Назад
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? 'входим…' : 'Войти'} <Arrow>→</Arrow>
              </Button>
            </Foot>
          </form>
        </Card>
      </Container>
    </View>
  );
};
